import { DateTime } from 'luxon';
import { useVoucher } from '../../../context/VoucherProdiver';
import { DiscountTypeMap, Entity } from '../../../types/Voucher';
import { deriveCreationTimezoneOffset } from '../../../helpers/dateTime';

import InfoDisplay from '../InfoDisplay';
import DiscountTypeFields from './DiscountTypeFields';

import {
  AllowedPlatformsRule,
  FreeAncillariesMap,
  FreeAncillariesRule,
} from '../../../types/Rules';

import styles from '../voucher-info.module.scss';

const EntityLabelMap: Record<Entity, string> = {
  flix_inc: 'Flix Inc',
  flix_mexico: 'Flix Mexico',
  kamil_koc: 'Kamil Koc',
  flix_brazil: 'Flix Brazil',
  flix_chile: 'Flix Chile',
  flix_india: 'Flix India',
  flix_se: 'Flix SE',
};

const getEntityLabel = (entity: Entity | null | undefined): string => {
  if (!entity) return '—';
  return EntityLabelMap[entity] ?? entity;
};

const VoucherInfo = () => {
  const { data } = useVoucher();

  return (
    <div className={styles.container}>
      <InfoDisplay
        title='Voucher Information'
        fields={[
          {
            label: 'How many vouchers?',
            value: '1',
          },
          {
            label: 'Specification',
            value: data.specification,
          },
        ]}
      />
      <InfoDisplay
        title='Voucher Structure'
        fields={[
          {
            label: 'Voucher Prefix',
            value: data.prefix,
          },
        ]}
      />
      <InfoDisplay
        title='Discount'
        fields={[
          {
            label: 'Discount type',
            value: DiscountTypeMap[data.discountType.type],
          },
          {
            label: 'Added ancillaries',
            value: data.rules
              ?.filter((r): r is FreeAncillariesRule => r.type === 'free_ancillaries')[0]
              ?.ancillaries.map(a => FreeAncillariesMap[a])
              .join(', '),
          },
          ...DiscountTypeFields(data.discountType),
          {
            label: 'Contract',
            value: getEntityLabel(data.entity),
          },
        ]}
      />
      <InfoDisplay
        title='Booking'
        fields={[
          {
            label: 'Usage type',
            value: data.multiUsage ? 'Multiple' : 'Single',
          },
          {
            label: 'Usage count',
            value: (() => {
              const max = data.multiUsage ? (data.maxUsageCount ?? 'Unlimited') : 1;
              return `${data.usageCount} / ${max}`;
            })(),
          },
          {
            label: 'Allowed platforms',
            value: (() => {
              const platformLabels: Record<string, string> = {
                all: 'Web and App booking',
                web: 'Web booking',
                mobile_pax: 'Mobile App booking',
                offline_sales: 'Offline booking',
              };

              const allowedRule = data.rules?.find(
                (r): r is AllowedPlatformsRule => r.type === 'allowed_platforms',
              );
              const hasOffline = data.rules?.some(r => r.type === 'offline_sales');

              if (hasOffline) return platformLabels['offline_sales'];
              if (allowedRule?.platforms.length === 1)
                return platformLabels[allowedRule.platforms[0]] ?? '—';
              return platformLabels['all'];
            })(),
          },
          {
            label: 'Voucher valid from',
            value: (() => {
              // Derive timezone from validSince (start of day is more reliable)
              const { offsetString, offsetMinutes } = deriveCreationTimezoneOffset(
                data.validityInterval.validSince,
                false,
              );
              // Convert UTC time to the original timezone
              const dt = DateTime.fromISO(data.validityInterval.validSince, { zone: 'utc' }).plus({
                minutes: offsetMinutes,
              });
              return ` ${dt.toFormat('dd.MM.yyyy')}, ${dt.toFormat('HH:mm')} ${offsetString}`;
            })(),
          },
          {
            label: 'Voucher valid until',
            value: (() => {
              // Use the same timezone offset derived from validSince for consistency
              const { offsetString, offsetMinutes } = deriveCreationTimezoneOffset(
                data.validityInterval.validSince,
                false,
              );
              // Convert UTC time to the original timezone
              const dt = DateTime.fromISO(data.validityInterval.validUntil, { zone: 'utc' }).plus({
                minutes: offsetMinutes,
              });
              return ` ${dt.toFormat('dd.MM.yyyy')}, ${dt.toFormat('HH:mm')} ${offsetString}`;
            })(),
          },
        ]}
      />
    </div>
  );
};

export default VoucherInfo;
